import React, { useState, useEffect, useMemo, useRef } from "react";
import clsx from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import Drawer from "../common/Drawer";
import ProductDetailsDrawer from "./ProductDetailsDrawer";
import ComponentCard, { PerformanceBar } from "./ComponentCard";
import { FilterSection, FilterDropdown, ActiveFilters } from "./FilterSection";
import styles from "./ProductSelectionDrawer.module.css";
import { useProductGroupPrices } from "../../hooks/useProductGroupPrices";
import {
	useProductDrawerValidation,
	getProductValidationStatus,
	getProductValidationErrors,
	PRODUCT_DRAWER_VALIDATION_QUERY_KEY,
} from "../../hooks/useProductDrawerValidation";
import { useProductPerformance } from "../../hooks/useProductPerformance";
import type { ComponentPerformance } from "../../services/performanceService";
import { Product } from "../../types/product";
import type { ProductDto } from "../../types/validation";
import { STORE_DEFAULT_PRICE } from "../../constants/productSelection";
import { customGaEvent } from "../../utils/customGA.ts";
import { StringSystem } from "../../types/viewmodel.ts";
import { useIsB2B } from "../../hooks/useViewModel.ts";
import { selectPrice } from "../../utils/priceHelpers.ts";

interface ProductSelectionDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	productKind: string;
	performanceComponentType?: string | null;
	configBundleId: string;
	productGroupId: string;
	currentProductId: string | null;
	defaultProductName?: string | null;
	onProductSelect?: (
		productGroupId: string,
		productId: string | null,
		productData?: any,
	) => void;
	canSelectEmpty?: boolean;
	defaultProductId?: string;
	currentConfiguration?: ProductDto[];
	strings: StringSystem;
}

const ProductSelectionDrawer: React.FC<ProductSelectionDrawerProps> = ({
	isOpen,
	onClose,
	productKind,
	performanceComponentType = null,
	configBundleId,
	productGroupId,
	currentProductId,
	defaultProductName,
	onProductSelect,
	canSelectEmpty = false,
	defaultProductId,
	currentConfiguration = [],
	strings,
}) => {
	const queryClient = useQueryClient();
	const isB2B = useIsB2B();
	const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>(
		[],
	);
	const [selectedFamilies, setSelectedFamilies] = useState<string[]>([]);
	const [selectedProductId, setSelectedProductId] = useState<string | null>(
		currentProductId,
	);
	const [detailsProduct, setDetailsProduct] = useState<Product | null>(null);

	useEffect(() => {
		if (isOpen) {
			setSelectedProductId(currentProductId);
			queryClient.invalidateQueries({
				queryKey: [PRODUCT_DRAWER_VALIDATION_QUERY_KEY],
			});
		}
	}, [isOpen, currentProductId, queryClient]);

	const { data, isLoading, error } = useProductGroupPrices({
		configBundleId,
		productGroupId,
		productKind,
		enabled: isOpen,
	});

	const {
		products: allProducts = [],
		pricedProductMap = new Map(),
		manufacturers = [],
		performanceComponentType: pricesPerformanceComponentType = null,
	} = data ?? {};

	const hasStoredDefaultPrice = useRef(false);
	const hasTrackedViewRef = useRef(false);

	useEffect(() => {
		if (!isOpen) {
			hasStoredDefaultPrice.current = false;
			hasTrackedViewRef.current = false;
		}
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen || allProducts.length === 0 || hasTrackedViewRef.current)
			return;

		hasTrackedViewRef.current = true;

		customGaEvent({
			app: "kpc_config",
			event: "view_component_list",
			component_list_name: productKind,
			components: allProducts.map((p) => p.materialNumber).join(","),
		});
	}, [isOpen, allProducts, productKind]);

	useEffect(() => {
		if (
			!isOpen ||
			!currentProductId ||
			pricedProductMap.size === 0 ||
			!onProductSelect
		)
			return;
		if (hasStoredDefaultPrice.current) return;
		if (canSelectEmpty && currentProductId !== defaultProductId) return;

		const currentPricedProduct = pricedProductMap.get(currentProductId);
		if (currentPricedProduct?.price) {
			hasStoredDefaultPrice.current = true;

			const defaultPriceData = {
				net: currentPricedProduct.price.net ?? 0,
				gross: currentPricedProduct.price.gross ?? 0,
			};

			onProductSelect(productGroupId, STORE_DEFAULT_PRICE, {
				defaultPrice: defaultPriceData,
				defaultProductId: currentProductId,
			});
		}
	}, [
		isOpen,
		currentProductId,
		pricedProductMap,
		productGroupId,
		onProductSelect,
		canSelectEmpty,
		defaultProductId,
	]);

	const productsToValidate = useMemo((): ProductDto[] => {
		return allProducts.map((product) => ({
			id: product.materialNumber,
			componentTypeId: productGroupId,
			name: product.title,
		}));
	}, [allProducts, productGroupId]);

	const { data: validationData } = useProductDrawerValidation({
		configBundleId,
		currentConfiguration,
		productsToValidate,
		productIdToReplace: currentProductId,
		enabled: isOpen && productsToValidate.length > 0,
	});

	const products = useMemo(
		() =>
			allProducts.map((p) => ({
				id: p.materialNumber,
				name: p.title,
			})),
		[allProducts],
	);

	const performanceDefaultName = defaultProductName ?? "";

	const resolvedPerformanceComponentType =
		pricesPerformanceComponentType ?? performanceComponentType;

	const { data: performanceData } = useProductPerformance({
		defaultProductName: performanceDefaultName,
		products,
		productType: resolvedPerformanceComponentType,
		enabled:
			isOpen &&
			products.length > 0 &&
			!!performanceDefaultName &&
			!!resolvedPerformanceComponentType,
	});

	const performanceMap = useMemo(() => {
		if (!performanceData) return new Map<string, ComponentPerformance>();
		return new Map(performanceData.map((p) => [p.productId, p]));
	}, [performanceData]);

	const hasAnyPerformanceData = useMemo(() => {
		if (!performanceData || performanceData.length === 0) return false;
		return performanceData.some((p) => p.percentageDifference !== 0);
	}, [performanceData]);

	const maxAbsPercentageDifference = useMemo(() => {
		if (!performanceData || performanceData.length === 0) return 100;
		return Math.max(
			...performanceData.map((p) => Math.abs(p.percentageDifference)),
			1,
		);
	}, [performanceData]);

	const defaultProductPrice = useMemo(() => {
		if (!currentProductId || pricedProductMap.size === 0) {
			return null;
		}
		const currentPricedProduct = pricedProductMap.get(currentProductId);
		const price = currentPricedProduct?.price
			? selectPrice(currentPricedProduct.price, isB2B)
			: null;
		if (!price) {
			return null;
		}

		return price;
	}, [currentProductId, pricedProductMap, isB2B]);

	const filteredProducts = useMemo(() => {
		let filtered = allProducts;

		if (selectedManufacturers.length > 0) {
			filtered = filtered.filter((product) => {
				const pricedProduct = pricedProductMap.get(product.materialNumber);
				return (
					pricedProduct?.manufacturer?.name &&
					selectedManufacturers.includes(pricedProduct.manufacturer.name)
				);
			});
		}

		if (selectedFamilies.length > 0) {
		}

		return filtered;
	}, [allProducts, selectedManufacturers, selectedFamilies, pricedProductMap]);

	const handleProductSelect = (product: Product) => {
		setSelectedProductId(product.materialNumber);
		if (onProductSelect) {
			const pricedProduct = pricedProductMap.get(product.materialNumber);

			customGaEvent({
				app: "kpc_config",
				event: "component_changed",
				selected: product.materialNumber,
				preselected: currentProductId,
				value: pricedProduct?.priceDifference
					? selectPrice(pricedProduct.priceDifference, isB2B)
					: selectPrice(product.priceDifference ?? { net: 0, gross: 0 }, isB2B),
				index: filteredProducts.findIndex(
					(p) => p.materialNumber === product.materialNumber,
				),
				component_list_name: productKind,
			});

			const productData = pricedProduct
				? {
						description: pricedProduct.description,
						description2: pricedProduct.description2,
						title: pricedProduct.title,
						imageUrl: pricedProduct.imageUrl,
						availabilityQuantity: pricedProduct.availabilityQuantity,
						availabilityStatus: pricedProduct.availabilityStatus,
						calculatedStockQuantity: pricedProduct.calculatedStockQuantity,
						stockStatus: pricedProduct.stockStatus,
						priceDifference: pricedProduct.priceDifference || {
							net: 0,
							gross: 0,
						},
						price: pricedProduct.price || { net: 0, gross: 0 },
					}
				: {
						description: product.description,
						description2: product.meta[0],
						title: product.title,
						imageUrl: product.imageUrl,
						availabilityQuantity: 0,
						availabilityStatus: "Unknown",
						calculatedStockQuantity: 0,
						stockStatus: product.stockStatus,
						priceDifference: product.priceDifference || { net: 0, gross: 0 },
					};

			onProductSelect(productGroupId, product.materialNumber, productData);
			onClose();
		}
	};

	const handleSelectEmpty = () => {
		setSelectedProductId(null);
		if (onProductSelect) {
			onProductSelect(productGroupId, null);
		}
		onClose();
	};

	return (
		<>
			<Drawer isOpen={isOpen} onClose={onClose} title={`Endre ${productKind}`}>
				<div className={styles.drawerContent}>
					<FilterSection>
						<FilterDropdown
							label="Fabrikant"
							options={manufacturers}
							selectedValues={selectedManufacturers}
							onSelect={setSelectedManufacturers}
						/>
					</FilterSection>

					<ActiveFilters
						filters={[...selectedManufacturers, ...selectedFamilies]}
						onClear={(filter) => {
							if (selectedManufacturers.includes(filter)) {
								setSelectedManufacturers(
									selectedManufacturers.filter((m) => m !== filter),
								);
							}
							if (selectedFamilies.includes(filter)) {
								setSelectedFamilies(
									selectedFamilies.filter((f) => f !== filter),
								);
							}
						}}
					/>

				<div className={styles.productListSection}>
						<div className={styles.productListHeader}>
							<h3 className={styles.sectionTitle}>Velg produkt</h3>
							{isB2B && (
								<span className={styles.exclVatLabel}>
									{strings.product.configBundles.exclVatLabel}
								</span>
							)}
						</div>
						{isLoading && (
							<div className={styles.loadingMessage}>Loading products...</div>
						)}
						{error && (
							<div className={styles.errorMessage}>
								{error.message || "Failed to load products"}
							</div>
						)}
						{!isLoading && !error && (
							<>
								{canSelectEmpty && (
									<button
										className={clsx(styles.clearSelectionButton, {
											[styles.selectedClearSelectionButton]:
												selectedProductId === null,
										})}
										onClick={handleSelectEmpty}
										data-testid={`product-selection-${productGroupId}-select-none`}
									>
										<span>Ingen produkt</span>
										{defaultProductPrice !== null && (
											<div className={styles.priceSubtraction}>
												- {defaultProductPrice.toLocaleString("no-NO")},-
											</div>
										)}
									</button>
								)}
								<div className={styles.productList}>
									{filteredProducts.map((product, index) => {
										const isSelected =
											selectedProductId === product.materialNumber;

										const validationStatus = getProductValidationStatus(
											validationData,
											product.materialNumber,
										);

										const validationErrors = getProductValidationErrors(
											validationData,
											product.materialNumber,
										);

										const performanceScore =
											performanceMap.get(product.materialNumber) ?? null;

										return (
											<div key={index} className={styles.productCardWrapper}>
												<ComponentCard
													kind={product.kind}
													title={product.title}
													meta={product.meta}
													description={product.description}
													imageUrl={product.imageUrl}
													stockStatus={product.stockStatus}
													onReadMoreClick={() => setDetailsProduct(product)}
													onSelectClick={() => handleProductSelect(product)}
													selectButtonText={
														strings.product.configBundles.select
													}
													isInDrawer={true}
													materialNumber={product.materialNumber}
													isSelected={isSelected}
													validationStatus={validationStatus}
													validationErrors={validationErrors}
													priceDifference={product.priceDifference!}
												>
													{hasAnyPerformanceData &&
														performanceScore != null && (
															<PerformanceBar
																label={
																	strings.product.configBundles.performanceBar
																		.performance
																}
																percentageDifference={
																	performanceScore.percentageDifference
																}
																maxPercentage={maxAbsPercentageDifference}
																strings={strings}
															/>
														)}
												</ComponentCard>
											</div>
										);
									})}
								</div>
							</>
						)}
					</div>
				</div>
			</Drawer>

			{detailsProduct && (
				<ProductDetailsDrawer
					isOpen={!!detailsProduct}
					onClose={() => setDetailsProduct(null)}
					product={detailsProduct}
					isSelected={selectedProductId === detailsProduct.materialNumber}
					onProductSelect={() => handleProductSelect(detailsProduct)}
				/>
			)}
		</>
	);
};

export default ProductSelectionDrawer;